import { useState } from 'react';

import { isPhoneLayout } from 'lib/hooks/useWindowSize';
import { useUIContext } from 'lib/context/UIContext/UIContext';
import { useNewButtonStyle } from 'lib/hooks/useNewButtonStyle';
import { usePageCreateAction } from 'lib/hooks/usePageCreateAction';
import { useTableContext } from 'lib/context/TableContext/TableContext';
import { useIsShellHosted } from 'lib/context/ShellContext/ShellContext';
import { camelCaseToReadableFormat, ia, pluralize, singularize } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import Export from 'components/shared/Export/Export';
import Header from 'components/shared/Header/Header';
import Popover from 'components/shared/Popovers/Popover/Popover';
import PageHeader from 'components/shared/PageHeader/PageHeader';
import Typography from 'components/shared/Typography/Typography';

import { buildExportData } from '../lib/helpers';
import { SPECIAL_UPPERCASE_NAMES } from '../lib/constants';

import HeaderViews from './HeaderViews';

const TableHeader = ({
  category,
  title: titleProp,
  onModalToggle,
  hasNew,
  subtitle,
  actions,
  refresh,
  onRefresh,
  noTitle,
  compact,
  showExport = true,
  switchItems,
  views,
  rightActions,
  loading,
  newButtonText,
  newButtonProps = {},
  newButtonDisabledReason,
  statusPanelData = [],
  data,
  hasData = true,
  getExportData,
  type
}) => {
  const tableContext = useTableContext();

  const { filters, title, gridApi, updateFilters, resetFilters, changeFilters, ...rest } =
    tableContext;

  const { device } = useUIContext();

  const isHosted = useIsShellHosted();
  const isMobile = isPhoneLayout(device) && !isHosted;
  const newButtonStyle = useNewButtonStyle();

  const totalSelected = rest.selectedRows?.length;

  const isAcronym = SPECIAL_UPPERCASE_NAMES.includes(category?.toLowerCase());
  const categoryName = isAcronym ? category.toUpperCase() : camelCaseToReadableFormat(category);
  const singularName = singularize(categoryName);
  const newLabel = newButtonText || `New ${isAcronym ? singularName : singularName?.toLowerCase()}`;

  usePageCreateAction(
    isHosted && hasNew
      ? { label: newLabel, onClick: onModalToggle, disabled: !!newButtonDisabledReason }
      : null
  );

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh?.();
    } finally {
      setIsRefreshing(false);
    }
  };

  const smallDeviceOptions = [
    isMobile &&
      hasNew && {
        label: newLabel,
        onClick: onModalToggle,
        icon: 'add-square-mono',
        color: 'primary',
        disabled: !!newButtonDisabledReason,
        tooltip: newButtonDisabledReason
      },
    isMobile &&
      refresh && {
        label: 'Reload the list',
        onClick: handleRefresh,
        icon: 'refresh-mono',
        loading: isRefreshing
      }
  ].filter(Boolean);

  const options = smallDeviceOptions.filter(Boolean);
  const rightContent =
    typeof rightActions === 'function'
      ? rightActions({ category, loading, filters, setFilters: changeFilters, ...rest })
      : rightActions;

  const headerTitle = !noTitle && (titleProp || title || categoryName);
  const viewTabs = {
    viewCounts: data?.viewCounts,
    items: switchItems,
    category,
    filters,
    title,
    updateFilters,
    resetFilters,
    type
  };

  const actionItems = (
    <>
      {typeof actions === 'function'
        ? actions({
            category,
            filters,
            loading,
            setFilters: changeFilters,
            data,
            ...rest,
            gridApi
          })
        : actions}
      {totalSelected > 0 && actions && (
        <Typography
          variant="label"
          weight="medium"
          color="neutral-700"
          size="small"
          className="whitespace-nowrap">
          {totalSelected} {pluralize('item', totalSelected)} selected
        </Typography>
      )}
      {!isMobile && refresh && (
        <Button
          size="xs"
          variant="tertiary"
          style="outlined"
          color="neutral"
          icon="refresh-linear"
          iconColor="neutral"
          iconIsStroke
          tooltip="Reload the list"
          isLoading={isRefreshing}
          onClick={handleRefresh}
          data-qa={`refresh-${categoryName?.toLowerCase()}-button`}
        />
      )}
      {showExport && (loading || hasData) && (
        <Export
          showLabel
          useData={() =>
            buildExportData({ tableContext, category, getExportData, data, statusPanelData })
          }
        />
      )}
      {isHosted && rightContent}
      {!isMobile && hasNew && (
        <Button
          text={newLabel}
          icon="add-square-mono"
          size="xs"
          textClassName="whitespace-nowrap normal-case"
          data-qa={`new-${categoryName?.toLowerCase()}-button`}
          {...newButtonStyle}
          {...newButtonProps}
          isDisabled={!!newButtonDisabledReason}
          tooltip={newButtonDisabledReason}
          onClick={onModalToggle}
        />
      )}
    </>
  );

  if (isHosted) {
    return (
      <PageHeader
        title={headerTitle}
        subtitle={subtitle}
        views={views}
        viewTabs={viewTabs}
        actionsClassName="gap-3">
        {actionItems}
      </PageHeader>
    );
  }

  return (
    <Header
      title={headerTitle}
      compact={compact}
      subtitle={subtitle}
      childrenClassName="flex min-w-0 items-center gap-3 justify-between flex-1">
      <HeaderViews isHosted={false} titled={false} views={views} {...viewTabs} />
      <div className="flex flex-1 items-center justify-end gap-3">{actionItems}</div>
      {rightContent}
      {ia(options) && <Popover options={options} position="left" />}
    </Header>
  );
};

export default TableHeader;
